import * as THREE from 'three';
import { Noise } from './Noise.js';
import { SeededRandom } from '../utils/random.js';
import { hslToHex } from '../utils/math.js';
import { PLANET } from '../utils/constants.js';

/* ═══════════════════════════════════════════════════
 * Biome colour palettes per planet type.
 * Each band: { elev: 0‒1, color: [h, s, l] }
 * Bands are sorted low→high; colours are interpolated.
 * ═══════════════════════════════════════════════════ */
const BIOME_PALETTES = {
  rocky: {
    bands: [
      { elev: 0.00, color: [0.08, 0.25, 0.22] },
      { elev: 0.15, color: [0.07, 0.30, 0.32] },
      { elev: 0.35, color: [0.06, 0.35, 0.40] },
      { elev: 0.55, color: [0.05, 0.20, 0.48] },
      { elev: 0.75, color: [0.03, 0.12, 0.58] },
      { elev: 1.00, color: [0.00, 0.05, 0.80] },
    ],
    polar: [0.55, 0.12, 0.72],
  },
  forest: {
    bands: [
      { elev: 0.00, color: [0.28, 0.55, 0.18] },
      { elev: 0.12, color: [0.30, 0.62, 0.25] },
      { elev: 0.30, color: [0.33, 0.68, 0.33] },
      { elev: 0.50, color: [0.36, 0.55, 0.38] },
      { elev: 0.70, color: [0.10, 0.42, 0.42] },
      { elev: 0.85, color: [0.06, 0.18, 0.52] },
      { elev: 1.00, color: [0.00, 0.06, 0.84] },
    ],
    polar: [0.42, 0.22, 0.30],
    waterColor: [0.38, 0.50, 0.22],
    waterLevel: 0.20,
  },
  ocean: {
    bands: [
      { elev: 0.00, color: [0.58, 0.72, 0.18] },
      { elev: 0.28, color: [0.56, 0.66, 0.32] },
      { elev: 0.42, color: [0.14, 0.52, 0.55] },
      { elev: 0.50, color: [0.30, 0.54, 0.40] },
      { elev: 0.68, color: [0.32, 0.46, 0.44] },
      { elev: 0.84, color: [0.06, 0.22, 0.54] },
      { elev: 1.00, color: [0.00, 0.10, 0.84] },
    ],
    polar: [0.56, 0.18, 0.78],
    waterColor: [0.57, 0.72, 0.28],
    waterLevel: 0.35,
  },
  ice: {
    bands: [
      { elev: 0.00, color: [0.55, 0.32, 0.72] },
      { elev: 0.20, color: [0.57, 0.28, 0.78] },
      { elev: 0.40, color: [0.58, 0.36, 0.84] },
      { elev: 0.60, color: [0.54, 0.22, 0.68] },
      { elev: 0.80, color: [0.60, 0.16, 0.88] },
      { elev: 1.00, color: [0.58, 0.10, 0.94] },
    ],
    polar: [0.60, 0.10, 0.90],
  },
  lava: {
    bands: [
      { elev: 0.00, color: [0.04, 1.00, 0.52] },
      { elev: 0.12, color: [0.01, 0.92, 0.38] },
      { elev: 0.28, color: [0.98, 0.72, 0.14] },
      { elev: 0.48, color: [0.00, 0.52, 0.18] },
      { elev: 0.68, color: [0.02, 0.62, 0.24] },
      { elev: 0.85, color: [0.01, 0.32, 0.13] },
      { elev: 1.00, color: [0.05, 0.82, 0.48] },
    ],
    polar: [0.98, 0.38, 0.10],
  },
  desert: {
    bands: [
      { elev: 0.00, color: [0.10, 0.58, 0.48] },
      { elev: 0.20, color: [0.09, 0.64, 0.58] },
      { elev: 0.40, color: [0.11, 0.56, 0.52] },
      { elev: 0.60, color: [0.08, 0.48, 0.43] },
      { elev: 0.80, color: [0.06, 0.36, 0.38] },
      { elev: 1.00, color: [0.04, 0.22, 0.62] },
    ],
    polar: [0.08, 0.28, 0.52],
  },
  crystal: {
    bands: [
      { elev: 0.00, color: [0.76, 0.50, 0.28] },
      { elev: 0.18, color: [0.78, 0.62, 0.42] },
      { elev: 0.38, color: [0.80, 0.72, 0.54] },
      { elev: 0.58, color: [0.72, 0.56, 0.48] },
      { elev: 0.78, color: [0.83, 0.76, 0.64] },
      { elev: 1.00, color: [0.85, 0.82, 0.78] },
    ],
    polar: [0.72, 0.40, 0.54],
  },
  gas: {
    bands: [
      { elev: 0.00, color: [0.10, 0.58, 0.30] },
      { elev: 0.20, color: [0.12, 0.54, 0.38] },
      { elev: 0.40, color: [0.15, 0.62, 0.48] },
      { elev: 0.60, color: [0.08, 0.50, 0.44] },
      { elev: 0.80, color: [0.05, 0.58, 0.52] },
      { elev: 1.00, color: [0.10, 0.44, 0.58] },
    ],
    polar: [0.10, 0.28, 0.32],
  },
};

/* ═══════════════════════════════════════════════════
 * Planet Renderer
 * ═══════════════════════════════════════════════════ */
export class PlanetRenderer {
  constructor() {
    /** @type {Map<number, Noise>} */
    this._noiseCache = new Map();
  }

  /* ── noise helper ─────────────────────────────── */

  _getNoise(seed) {
    if (this._noiseCache.size > 30) this._noiseCache.clear();
    if (!this._noiseCache.has(seed)) {
      this._noiseCache.set(seed, new Noise(seed));
    }
    return this._noiseCache.get(seed);
  }

  /* ── terrain height (shared by mesh & physics) ── */

  /**
   * Multi-layered noise terrain height for a normalised direction.
   * Returns absolute radius (centre → surface).
   *
   * Layers:
   *  1. Continental shape   – domain-warped FBM
   *  2. Mountain ridges     – ridge noise, masked by continental
   *  3. Hills               – plain FBM
   *  4. Fine detail         – high-freq FBM
   *  5. Micro bumps         – single noise sample
   */
  getTerrainHeight(terrain, seed, radius, normalizedDir) {
    const noise = this._getNoise(seed);
    const rng = new SeededRandom(seed + 7777);

    const nx = normalizedDir.x;
    const ny = normalizedDir.y;
    const nz = normalizedDir.z;

    // Seed-derived offsets so layers don't correlate
    const ox = rng.range(-50, 50);
    const oy = rng.range(-50, 50);
    const oz = rng.range(-50, 50);

    const S = terrain.scale;          // base frequency multiplier
    const H = terrain.height * radius; // max displacement
    const O = Math.max(3, terrain.octaves); // octave count hint

    // 1. Continental (domain-warped for organic coastlines)
    const cS = S * 1.2;
    const continental = noise.warp(
      nx * cS + ox, ny * cS + oy, nz * cS + oz,
      0.45, Math.min(O, 4),
    );

    // 2. Mountain ridges — only where continental is high
    const mS = S * 3.5;
    const mountainMask = Math.max(0, continental * 0.8 + 0.25);
    const mountains = noise.ridge(
      nx * mS + ox + 100, ny * mS + oy + 100, nz * mS + oz + 100,
      Math.min(O, 5), 2.2, 0.48, 2.0,
    ) * mountainMask;

    // 3. Rolling hills
    const hS = S * 6;
    const hills = noise.fbm(
      nx * hS + ox + 200, ny * hS + oy + 200, nz * hS + oz + 200,
      Math.min(O - 1, 4), 2.0, 0.5,
    ) * 0.35;

    // 4. Fine detail
    const dS = S * 14;
    const detail = noise.fbm(
      nx * dS + ox + 300, ny * dS + oy + 300, nz * dS + oz + 300,
      Math.min(O - 2, 3), 2.4, 0.45,
    ) * 0.12;

    // 5. Micro bumps
    const uS = S * 35;
    const micro = noise.noise3D(
      nx * uS + ox + 400, ny * uS + oy + 400, nz * uS + oz + 400,
    ) * 0.04;

    const total = (continental * 0.42 + mountains * 0.28 + hills + detail + micro) * H;
    return radius + total;
  }

  /* ── surface helpers ──────────────────────────── */

  getSurfacePosition(planetData, direction) {
    const dir = direction.clone().normalize();
    const radius = PLANET.SURFACE_RADIUS * planetData.size;
    const h = this.getTerrainHeight(planetData.terrain, planetData.seed, radius, dir);
    return dir.multiplyScalar(h);
  }

  /* ── main mesh builder ────────────────────────── */

  createPlanetMesh(planetData, detail = 64) {
    const group = new THREE.Group();
    group.userData.planetData = planetData;

    const rng = new SeededRandom(planetData.seed);
    const { visual, terrain, size, type } = planetData;
    const radius = PLANET.SURFACE_RADIUS * size;

    // ── Main sphere ──
    const isDistant = detail <= 16;
    const subdivisions = isDistant ? 2 : (detail > 32 ? 6 : 5);
    const geo = new THREE.IcosahedronGeometry(radius, subdivisions);

    const matOpts = isDistant
      ? {
          color: hslToHex(visual.hue, visual.saturation, visual.lightness),
          roughness: 0.95,
          metalness: 0.0,
          flatShading: true,
          vertexColors: false,
        }
      : {
          roughness: type === 'ice' ? 0.35 : 0.75,
          metalness: type === 'crystal' ? 0.35 : (type === 'lava' ? 0.18 : 0.05),
          flatShading: false,
          vertexColors: true,
        };

    if (!isDistant) {
      this._applyTerrain(geo, terrain, planetData.seed, radius);
      this._applyBiomeColors(geo, planetData, radius);
    }

    const mat = new THREE.MeshStandardMaterial(matOpts);

    // Type-specific emissive
    if (!isDistant) {
      if (type === 'lava') {
        mat.emissive = new THREE.Color(0xff3300);
        mat.emissiveIntensity = 0.45;
      } else if (type === 'crystal') {
        mat.emissive = hslToHex(visual.hue, 0.5, 0.3);
        mat.emissiveIntensity = 0.25;
      }
    }

    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = 'planet-surface';
    group.add(mesh);

    // ── Water layer (ocean / forest) ──
    if (!isDistant) {
      const palette = BIOME_PALETTES[type];
      if (palette && palette.waterLevel) {
        const wR = radius * (1 + terrain.height * (palette.waterLevel - 0.5) * 0.5);
        const wGeo = new THREE.IcosahedronGeometry(wR, 4);
        const wc = palette.waterColor || [0.55, 0.6, 0.35];
        const wMat = new THREE.MeshStandardMaterial({
          color: hslToHex(wc[0], wc[1], wc[2]),
          transparent: true,
          opacity: 0.65,
          roughness: 0.08,
          metalness: 0.35,
          depthWrite: false,
        });
        const water = new THREE.Mesh(wGeo, wMat);
        water.name = 'water-layer';
        group.add(water);
      }
    }

    // ── Atmosphere ──
    if (visual.hasAtmosphere && !isDistant) {
      this._addAtmosphere(group, radius, visual, type);
    }

    // ── Rings ──
    if (visual.hasRings && !isDistant) {
      this._addRings(group, radius, visual, rng);
    }

    return group;
  }

  createPreviewMesh(planetData) {
    const { visual, size } = planetData;
    const radius = size * 2;
    const geo = new THREE.IcosahedronGeometry(radius, 2);
    const color = hslToHex(visual.hue, visual.saturation, visual.lightness);
    return new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
      color, roughness: 0.7, metalness: 0.2,
    }));
  }

  /* ── terrain displacement ─────────────────────── */

  _applyTerrain(geometry, terrain, seed, radius) {
    const positions = geometry.attributes.position;
    const v = new THREE.Vector3();

    for (let i = 0; i < positions.count; i++) {
      v.fromBufferAttribute(positions, i).normalize();
      const h = this.getTerrainHeight(terrain, seed, radius, v);
      v.multiplyScalar(h);
      positions.setXYZ(i, v.x, v.y, v.z);
    }

    positions.needsUpdate = true;
    geometry.computeVertexNormals();
  }

  /* ── biome-based vertex colours ───────────────── */

  _applyBiomeColors(geometry, planetData) {
    const { type, seed, visual } = planetData;
    const noise = this._getNoise(seed);
    const rng  = new SeededRandom(seed + 42424);
    const positions = geometry.attributes.position;
    const colors = new Float32Array(positions.count * 3);
    const vertex = new THREE.Vector3();
    const color  = new THREE.Color();

    const palette = BIOME_PALETTES[type] || BIOME_PALETTES.rocky;
    const bands   = palette.bands;

    // Randomised pole axis for latitude variation
    const pole = new THREE.Vector3(
      rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1),
    ).normalize();

    // Pre-pass: find height range for normalisation
    let minH = Infinity, maxH = -Infinity;
    for (let i = 0; i < positions.count; i++) {
      const d = new THREE.Vector3().fromBufferAttribute(positions, i).length();
      if (d < minH) minH = d;
      if (d > maxH) maxH = d;
    }
    const hRange = maxH - minH || 1;

    for (let i = 0; i < positions.count; i++) {
      vertex.fromBufferAttribute(positions, i);
      const dir  = vertex.clone().normalize();
      const dist = vertex.length();

      const elevation = (dist - minH) / hRange;            // 0→1
      const latitude  = Math.abs(dir.dot(pole));            // 0 equator, 1 pole

      // Find bracketing bands & interpolate
      let lo = bands[0], hi = bands[bands.length - 1];
      for (let b = 0; b < bands.length - 1; b++) {
        if (elevation >= bands[b].elev && elevation <= bands[b + 1].elev) {
          lo = bands[b]; hi = bands[b + 1]; break;
        }
      }
      const bT = (elevation - lo.elev) / (hi.elev - lo.elev || 1);

      let h = lo.color[0] + (hi.color[0] - lo.color[0]) * bT;
      let s = lo.color[1] + (hi.color[1] - lo.color[1]) * bT;
      let l = lo.color[2] + (hi.color[2] - lo.color[2]) * bT;

      // Polar tint
      if (palette.polar && latitude > 0.65) {
        const pt = ((latitude - 0.65) / 0.35);
        const pp = pt * pt;
        h += (palette.polar[0] - h) * pp;
        s += (palette.polar[1] - s) * pp;
        l += (palette.polar[2] - l) * pp;
      }

      // Noise-driven variation
      const cn = noise.fbm(dir.x * 8 + 500, dir.y * 8 + 500, dir.z * 8 + 500, 3) * 0.08;
      const dn = noise.noise3D(dir.x * 22 + 600, dir.y * 22 + 600, dir.z * 22 + 600) * 0.04;
      h += cn * 0.04;
      s = Math.max(0, Math.min(1, s + cn));
      l = Math.max(0, Math.min(1, l + dn));

      // Shift by planet's unique hue
      h += (visual.hue - 0.5) * 0.12;
      h = ((h % 1) + 1) % 1;

      color.setHSL(h, Math.max(0, Math.min(1, s)), Math.max(0, Math.min(1, l)));
      colors[i * 3]     = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  }

  /* ── atmosphere ───────────────────────────────── */

  _addAtmosphere(group, radius, visual, type) {
    let aH = visual.atmosphereHue, aS = 0.42, aL = 0.58, aO = 0.12;
    switch (type) {
      case 'lava':    aH = 0.02; aS = 0.78; aL = 0.38; aO = 0.20; break;
      case 'ice':     aH = 0.58; aS = 0.30; aL = 0.68; aO = 0.10; break;
      case 'forest':  aH = 0.35; aS = 0.42; aL = 0.48; aO = 0.15; break;
      case 'ocean':   aH = 0.55; aS = 0.48; aL = 0.58; aO = 0.14; break;
      case 'crystal': aH = 0.78; aS = 0.38; aL = 0.55; aO = 0.12; break;
    }
    const ac = hslToHex(aH, aS, aL);

    // Inner glow
    const innerGeo = new THREE.IcosahedronGeometry(radius * PLANET.ATMOSPHERE_SCALE, 3);
    const innerMat = new THREE.MeshStandardMaterial({
      color: ac, transparent: true, opacity: aO,
      side: THREE.BackSide, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    inner.name = 'atmosphere';
    group.add(inner);

    // Outer haze
    const outerGeo = new THREE.IcosahedronGeometry(radius * (PLANET.ATMOSPHERE_SCALE + 0.06), 2);
    const outerMat = new THREE.MeshStandardMaterial({
      color: ac, transparent: true, opacity: aO * 0.35,
      side: THREE.BackSide, depthWrite: false,
    });
    const outer = new THREE.Mesh(outerGeo, outerMat);
    outer.name = 'atmosphere-haze';
    group.add(outer);
  }

  /* ── rings with colour bands ──────────────────── */

  _addRings(group, radius, visual, rng) {
    const innerR = radius * 1.3;
    const outerR = radius * 2.0;
    const ringGeo = new THREE.RingGeometry(innerR, outerR, 128, 4);
    const positions = ringGeo.attributes.position;
    const rc = new Float32Array(positions.count * 3);
    const color = new THREE.Color();

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i), y = positions.getY(i);
      const t = (Math.sqrt(x * x + y * y) - innerR) / (outerR - innerR);
      const band = Math.sin(t * 22 + rng.range(0, 10)) * 0.5 + 0.5;
      color.setHSL(
        visual.ringColor + band * 0.05,
        0.28 + band * 0.22,
        0.28 + t * 0.28 + band * 0.10,
      );
      rc[i * 3] = color.r; rc[i * 3 + 1] = color.g; rc[i * 3 + 2] = color.b;
    }
    ringGeo.setAttribute('color', new THREE.BufferAttribute(rc, 3));

    const ringMat = new THREE.MeshStandardMaterial({
      vertexColors: true, transparent: true, opacity: 0.42,
      side: THREE.DoubleSide, depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = Math.PI / 2 + rng.range(-0.3, 0.3);
    ring.name = 'rings';
    group.add(ring);
  }
}
